import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import type { AdminUserDto, Paged } from '@mafia/core';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { Avatar } from '@/components/Avatar';
import { Alert, Badge, Card, Empty, Input, Pagination, Spinner } from '@/components/ui';

const FILTERS = [
  { id: 'all', label: 'Все' },
  { id: 'online', label: 'Онлайн' },
  { id: 'banned', label: 'Заблокированные' },
  { id: 'admins', label: 'Админы' },
] as const;

export function AdminUsers() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const filter = params.get('filter') ?? 'all';
  const page = Number(params.get('page') ?? 1);
  const [search, setSearch] = useState(q);

  // Поиск с задержкой, чтобы не дёргать API на каждую букву.
  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== q) setParams((p) => (search ? p.set('q', search) : p.delete('q'), p.delete('page'), p));
    }, 300);
    return () => clearTimeout(t);
  }, [search, q, setParams]);

  const users = useQuery({
    queryKey: ['admin', 'users', q, filter, page],
    queryFn: () => api<Paged<AdminUserDto>>(`/api/admin/users?${new URLSearchParams({ q, filter, page: String(page) })}`),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input className="pl-9" placeholder="ID, логин, email или ник" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setParams((p) => (p.set('filter', f.id), p.delete('page'), p))}
              className={clsx('whitespace-nowrap rounded-lg px-3 py-2 text-sm', filter === f.id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white')}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        {users.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : users.isError ? (
          <div className="p-4">
            <Alert>Не удалось загрузить пользователей</Alert>
          </div>
        ) : users.data!.items.length === 0 ? (
          <Empty title="Никого не нашли" />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Email</th>
                  <th>Статус</th>
                  <th>Регистрация</th>
                </tr>
              </thead>
              <tbody>
                {users.data!.items.map((u) => (
                  <tr key={u.id} className="hover:bg-white/[.02]">
                    <td>
                      <Link to={`/admin/users/${u.id}`} className="flex items-center gap-2.5 hover:text-gold-300">
                        <Avatar id={u.avatarId} name={u.nickname} size={30} />
                        <span>
                          <span className="block font-medium">{u.nickname}</span>
                          <span className="block text-xs text-zinc-500">@{u.login}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="text-zinc-400">{u.email}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {u.role === 'ADMIN' && <Badge tone="gold">админ</Badge>}
                        {u.isBanned && <Badge tone="red">бан</Badge>}
                        {u.online && <Badge tone="green">онлайн</Badge>}
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-zinc-500">{formatDate(u.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {users.data && (
        <Pagination
          page={users.data.page}
          pageSize={users.data.pageSize}
          total={users.data.total}
          onPage={(n) => setParams((p) => (p.set('page', String(n)), p))}
        />
      )}
    </div>
  );
}
